import { Injectable, OnDestroy, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, combineLatest, of, Subject } from 'rxjs';
import { map, shareReplay, tap, catchError, takeUntil } from 'rxjs/operators';

import { IndexDataService, IndexBase, IndexRegistry } from '@core/services/indexation/index-data.service';
import { AuthStore } from '@core/services/auth/auth.store';

export interface IndexOption {
  id: string;
  name: string;
  country: string;
  baseYear: string;
  isActive: boolean;
  scope: 'GLOBAL' | 'COUNTRY' | 'REGION';
  lastRefresh: Date;
}

export interface IndexValueLookup {
  period: string;
  value: number;
  indexId: string;
  indexName: string;
}

/**
 * Global service for managing index data throughout the application
 * Loads index data on startup and provides methods for contracts and other features
 */
@Injectable({
  providedIn: 'root'
})
export class GlobalIndexService implements OnDestroy {
  private destroy$ = new Subject<void>();
  
  private indexesSubject = new BehaviorSubject<IndexBase[]>([]);
  private registriesSubject = new BehaviorSubject<IndexRegistry[]>([]);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  // Public observables
  public indexes$ = this.indexesSubject.asObservable();
  public registries$ = this.registriesSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();

  // Computed observables
  public activeIndexes$ = this.indexes$.pipe(
    map(indexes => indexes.filter(index => index.isActive)),
    shareReplay(1)
  );

  public indexOptions$ = this.activeIndexes$.pipe(
    map(indexes => indexes.map(index => ({
      id: index.id,
      name: index.name,
      country: index.country,
      baseYear: index.baseYear,
      isActive: index.isActive,
      scope: index.scope,
      lastRefresh: index.lastRefresh
    } as IndexOption))),
    shareReplay(1)
  );

  private authStore = inject(AuthStore);
  private isAuthenticated$ = toObservable(this.authStore.isAuthenticated);

  constructor(
    private indexDataService: IndexDataService,
  ) {
    // Listen for logout events and clean up data
    this.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAuthenticated => {
        if (!isAuthenticated) {
          this.clearData();
        }
      });
  }

    /**
   * Initialize the service - call this on app startup
   */
  initialize(): Observable<boolean> {
    // console.log('🚀 GlobalIndexService: Starting initialization...');
    this.isLoadingSubject.next(true);
    
    // Try the full registry approach first, fallback to direct loading if permissions fail
    return combineLatest([
      this.indexDataService.getIndexRegistries().pipe(
        // tap(registries => console.log('📋 Registries loaded:', registries)),
        catchError(error => {
          console.warn('⚠️ Registry access failed, using fallback approach:', error.message);
          return of([]);
        })
      ),
      this.indexDataService.getAllIndexBases().pipe(
        // tap(indexes => console.log('📊 Index bases loaded:', indexes)),
        catchError(error => {
          console.warn('⚠️ Registry-based index loading failed, trying direct approach:', error.message);
          // Fallback: try to load Belgium Health Index directly
          return this.indexDataService.getBelgiumHealthIndexBase2013().pipe(
            map(belgiumIndex => belgiumIndex ? [belgiumIndex] : []),
            tap(indexes => console.log('📊 Direct Belgium index loaded:', indexes))
          );
        })
      )
    ]).pipe(
      tap(([registries, indexes]) => {
        // console.log('🔄 Processing loaded data...', { registries: registries.length, indexes: indexes.length });
        
        this.registriesSubject.next(registries);
        this.indexesSubject.next(indexes);
        this.isLoadingSubject.next(false);
        
        // console.log('✅ GlobalIndexService initialized with:', { registries: registries.length, indexes: indexes.length, indexNames: indexes.map(idx => idx.name) });
      }),
      catchError(error => {
        console.error('❌ GlobalIndexService initialization completely failed:', error);
        this.isLoadingSubject.next(false);
        // Return empty arrays on error but still complete successfully
        this.registriesSubject.next([]);
        this.indexesSubject.next([]);
        return of(false);
      }),
      map(() => true),
      shareReplay(1)
    );
  }

  /**
   * Refresh all index data
   */
  refresh(): Observable<boolean> {
    return this.initialize();
  }

  /**
   * Get available index options for UI selection
   */
  getIndexOptions(): Observable<IndexOption[]> {
    return this.indexOptions$;
  }

  /**
   * Get index value for a specific period (YYYY-MM format) and index ID
   */
  getIndexValue(indexId: string, period: string): Observable<IndexValueLookup | null> {
    return this.indexes$.pipe(
      map(indexes => {
        const index = indexes.find(idx => idx.id === indexId);
        if (!index) {
          return null;
        }

        const values = this.indexDataService.convertValuesToArray(index.values);
        const value = values.find(v => v.period === period);
        
        if (!value) {
          return null;
        }

        return {
          period: value.period,
          value: value.value,
          indexId: index.id,
          indexName: index.name
        } as IndexValueLookup;
      })
    );
  }

  /**
   * Get index value by date (will convert to YYYY-MM format)
   */
  getIndexValueByDate(indexId: string, date: Date): Observable<IndexValueLookup | null> {
    const period = this.formatDateToPeriod(date);
    return this.getIndexValue(indexId, period);
  }

  /**
   * Get the most recent index value for an index
   */
  getLatestIndexValue(indexId: string): Observable<IndexValueLookup | null> {
    return this.indexes$.pipe(
      map(indexes => {
        const index = indexes.find(idx => idx.id === indexId);
        if (!index) {
          return null;
        }

        const values = this.indexDataService.convertValuesToArray(index.values);
        if (values.length === 0) {
          return null;
        }

        // Values are already sorted newest first
        const latestValue = values[0];
        
        return {
          period: latestValue.period,
          value: latestValue.value,
          indexId: index.id,
          indexName: index.name
        } as IndexValueLookup;
      })
    );
  }

  /**
   * Get all available periods for an index (sorted newest first)
   */
  getAvailablePeriods(indexId: string): Observable<string[]> {
    return this.indexes$.pipe(
      map(indexes => {
        const index = indexes.find(idx => idx.id === indexId);
        if (!index) {
          return [];
        }

        const values = this.indexDataService.convertValuesToArray(index.values);
        return values.map(v => v.period);
      })
    );
  }

  /**
   * Search for index values within a date range
   */
  getIndexValuesInRange(indexId: string, startDate: Date, endDate: Date): Observable<IndexValueLookup[]> {
    return this.indexes$.pipe(
      map(indexes => {
        const index = indexes.find(idx => idx.id === indexId);
        if (!index) {
          return [];
        }

        const startPeriod = this.formatDateToPeriod(startDate);
        const endPeriod = this.formatDateToPeriod(endDate);
        
        const values = this.indexDataService.convertValuesToArray(index.values);
        
        return values
          .filter(v => v.period >= startPeriod && v.period <= endPeriod)
          .map(v => ({
            period: v.period,
            value: v.value,
            indexId: index.id,
            indexName: index.name
          } as IndexValueLookup));
      })
    );
  }

  /**
   * Get Belgium Health Index specifically (most commonly used)
   */
  getBelgiumHealthIndex(): Observable<IndexBase | null> {
    return this.indexes$.pipe(
      map(indexes => indexes.find(idx => 
        idx.country === 'BE' && 
        idx.name.toLowerCase().includes('health') &&
        idx.isActive
      ) || null)
    );
  }

  /**
   * Helper method to format Date to YYYY-MM period string
   */
  private formatDateToPeriod(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  /**
   * Helper method to parse period string to Date
   */
  public parsePeriodToDate(period: string): Date {
    const [year, month] = period.split('-').map(Number);
    return new Date(year, month - 1, 1); // First day of the month
  }

  /**
   * Format period for display (e.g., "2024-01" -> "January 2024")
   */
  public formatPeriodForDisplay(period: string): string {
    const date = this.parsePeriodToDate(period);
    return date.toLocaleDateString('fr-BE', { year: 'numeric', month: 'long' });
  }

  /**
   * Get current cached indexes (synchronous)
   */
  getCurrentIndexes(): IndexBase[] {
    return this.indexesSubject.value;
  }

  /**
   * Synchronously resolve the index value for `date`'s period (YYYY-MM) from the
   * cached table, walking back up to 24 months when that exact period isn't
   * published yet (the "most recent value not later than the date" rule).
   * Returns null when the index or any nearby value is unavailable.
   */
  getIndexValueAtOrBefore(indexId: string, date: Date): number | null {
    const index = this.indexesSubject.value.find(i => i.id === indexId);
    if (!index?.values) return null;
    const cursor = new Date(date.getFullYear(), date.getMonth(), 1);
    for (let i = 0; i < 24; i++) {
      const period = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
      const value = index.values[period];
      if (typeof value === 'number') return value;
      cursor.setMonth(cursor.getMonth() - 1);
    }
    return null;
  }

  /**
   * Resolve the index value for the EXACT period of `date` (YYYY-MM), without
   * walking back. Returns null when that period isn't published yet — used to
   * detect "too early" indexations (the reference index isn't out).
   */
  getIndexValueExact(indexId: string, date: Date): number | null {
    const index = this.indexesSubject.value.find(i => i.id === indexId);
    if (!index?.values) return null;
    const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const value = index.values[period];
    return typeof value === 'number' ? value : null;
  }

  /**
   * Pick an active index id whose name matches one of `keywords` (case-insensitive,
   * in priority order), falling back to the first active index. Used to map a
   * contract's indexation TYPE to a concrete index in the table when the contract
   * itself doesn't persist the index id.
   */
  findActiveIndexId(keywords: string[]): string | null {
    const active = this.indexesSubject.value.filter(i => i.isActive);
    for (const keyword of keywords) {
      const match = active.find(i => i.name?.toLowerCase().includes(keyword.toLowerCase()));
      if (match) return match.id;
    }
    return active[0]?.id ?? null;
  }

  /**
   * Get current cached registries (synchronous)
   */
  getCurrentRegistries(): IndexRegistry[] {
    return this.registriesSubject.value;
  }

  /**
   * Check if data is currently being loaded
   */
  isCurrentlyLoading(): boolean {
    return this.isLoadingSubject.value;
  }

  /**
   * Clear all data when user logs out
   */
  private clearData(): void {
    // console.log('🧹 GlobalIndexService: Clearing data due to logout');
    this.registriesSubject.next([]);
    this.indexesSubject.next([]);
    this.isLoadingSubject.next(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
} 